import React, { useEffect, useRef } from 'react';
import { PropTypes } from 'prop-types';
import { Line } from '@ant-design/plots';
import moment from 'moment';
import { useIntl } from 'react-intl';
import chartConfig from './index.config';
import { ChartsColors } from '../../../../../../../constants/charts';
import { extractChannelIdentifiers } from '../../../../../../../utils/extractChannelIdentifiers';
import useChartResize from '../../../../../../../hooks/useChartResize';
import styles from './index.module.scss';

const { calibration } = styles;

const ResistanceChart = ({ loading, data, monitoring, range }) => {
  const intl = useIntl();
  const plot = useRef();
  const channelNames = useRef(
    extractChannelIdentifiers(data, 'resistance').filter(
      (channel) => channel !== 5,
    ),
  );
  const selectedChannels = useRef([...channelNames.current]);
  const sliderRange = useRef([0, 1]);
  
  // Hook para redimensionar el gráfico cuando el contenedor cambie de tamaño
  const containerRef = useChartResize(plot, [data, range]);

  useEffect(() => {
    const handler = ({ target }) => {
      const { id, name } = target.cfg;
      const match = id.match(/ch(\d+)/);
      if (match && name.includes('name')) {
        const channel = parseInt(match[1], 10);
        if (selectedChannels.current.includes(channel)) {
          selectedChannels.current = selectedChannels.current.filter(
            (c) => c !== channel,
          );
        } else {
          selectedChannels.current = [...selectedChannels.current, channel];
        }
      }
      if (match && name.includes('radio')) {
        const channel = parseInt(match[1], 10);
        if (selectedChannels.current.includes(channel)) {
          selectedChannels.current = selectedChannels.current.filter(
            (c) => c === channel,
          );
        } else {
          selectedChannels.current = [...channelNames.current];
        }
      }
    };
    const _plot = plot.current;
    _plot.getChart().on('legend-item:click', handler);

    return () => _plot.getChart().off('legend-item:click', handler);
  }, []);

  useEffect(() => {
    const _plot = plot.current;
    const handler = ({ view: { controllers } }) => {
      const { end, start } = controllers[4];
      sliderRange.current = [start, end];
    };
    _plot.getChart().on('slider:mouseup', handler);

    return () => {
      _plot.getChart().off('slider:mouseup', handler);
    };
  }, []);

  useEffect(() => {
    const channels = extractChannelIdentifiers(data, 'resistance').filter(
      (channel) => channel !== 5,
    );
    channelNames.current = channels;
    if (selectedChannels.current.length === 0) {
      selectedChannels.current = channels;
    }
  }, [data]);

  useEffect(() => {
    sliderRange.current = [0, 1];
  }, [range]);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <p className={calibration}>
        {monitoring.mixture_data.equations.equation}
      </p>
      <Line
        {...chartConfig}
        data={data.filter((d) => d.channel !== 'ch5')}
        loading={loading}
        xAxis={{
          title: {
            text: intl.formatMessage({
              id: 'strength.thermalcontrol.monitorings.view.charts.date',
            }),
          },
          label: {
            offset: 10,
          },
        }}
        yAxis={{
          title: {
            text: intl.formatMessage({
              id: 'strength.thermalcontrol.monitorings.view.charts.resistance',
            }),
          },
        }}
        legend={{
          offsetX: 35,
          layout: 'horizontal',
          position: 'top-left',
          custom: true,
          selected: channelNames.current.reduce((acc, channel) => {
            acc[`ch${channel}`] = selectedChannels.current.includes(channel);
            return acc;
          }, {}),
          itemName: {
            formatter: (v) => monitoring[v],
          },
          items: channelNames.current.map((i) => ({
            name: `ch${i}`,
            value: `ch${i}`,
            id: `ch${i}`,
            marker: {
              symbol: 'hyphen',
              stroke: ChartsColors[+i - 1],
              style: { stroke: ChartsColors[+i - 1], lineWidth: 2 },
            },
            unchecked: !selectedChannels.current.includes(i),
          })),
        }}
        tooltip={{
          formatter: ({ channel, timestamp, resistance }) => {
            if (resistance) {
              return {
                name: monitoring[channel],
                title: moment(timestamp).format('DD/MM/YYYY HH:mm'),
                value: resistance + 'Mpa',
              };
            }
            return undefined;
          },
          customItems: (originalItems) => {
            const items = originalItems.filter(
              ({ data: { channel } }) => channel !== 'ch5',
            );
            return items.sort((a, b) =>
              a.data.channel.localeCompare(b.data.channel),
            );
          },
        }}
        slider={{
          formatter: (v) => moment(v).format('DD/MM/YYYY'),
          foregroundStyle: { fill: '#ff000090' },
          height: 30,
          textStyle: { fontWeight: 600, fill: 'black' },
          start: sliderRange.current[0],
          end: sliderRange.current[1],
        }}
        ref={plot}
      />
    </div>
  );
};

ResistanceChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      channel: PropTypes.string,
      maturity: PropTypes.number,
      resistance: PropTypes.number,
      timestamp: PropTypes.string,
    }),
  ),
  loading: PropTypes.bool,
  monitoring: PropTypes.object.isRequired,
  range: PropTypes.arrayOf(PropTypes.instanceOf(moment)).isRequired,
};

ResistanceChart.defaultProps = {
  loading: false,
  data: [],
};

export default React.memo(ResistanceChart);
